<script setup lang="ts">
import type { ComputedOptions, ConcreteComponent, FunctionalComponent, MethodOptions } from 'vue';
import type { Speciality } from '../../types/courses';
import { SpecialityLabels } from '../../types/courses';
import { SubscriptionType } from "~/composables/useAuthentication";
import BadgeFreeCmeCourse from '@/assets/images/icons/badge-free-cme.svg';

export interface MCourseCardProps {
  id: number;
  type: string;
  targetUrl?: string;
  title: string;
  specialization: Speciality[];
  teacherIllustration?: string;
  cmeCredits?: number;
  backgroundImage: string;
  teachers: string[];
  progress?: number;
  lessons?: number;
  cases?: number;
  durationMinutes: number;
  quizzes?: number;
  element?: "a" | "article" | "dialog" | "div" | ConcreteComponent<{}, any, any, ComputedOptions, MethodOptions> | FunctionalComponent<{}, any, any>;
  isFreeCmeCourse?: boolean;
}

const props = defineProps<MCourseCardProps>();

const formatHours = (h: number) => `${h}h`;
const formatMinutes = (m: number) => `${m}m`;

const { user } = useUserStore();

const showFreeCmeBadge = computed(() => {
  const isFreeCmeCourse = props.isFreeCmeCourse;
  const subscriptionType = user.value?.subscription.type;

  return isFreeCmeCourse &&
      [SubscriptionType.basic, SubscriptionType.trial].includes(subscriptionType as SubscriptionType);
});

const durationPretty = computed(() => {
  if (!props.durationMinutes || typeof props.durationMinutes !== 'number') {
    return '';
  }

  const hours = Math.floor(props.durationMinutes / 60);
  if (hours) {
    const remaining = props.durationMinutes - (hours * 60);
    return remaining ? formatHours(hours) + ' ' + formatMinutes(remaining) : formatHours(hours);
  }

  return formatMinutes(props.durationMinutes);
});

const authors = computed(() => {
  if (!props.teachers) {
    return 'unknown';
  }

  const teachers: string[] = props.teachers;

  // if the array is empty, return an empty string
  if (teachers.length === 0) {return '';}

  // if the array has one item, return that item
  if (teachers.length === 1) {return teachers[0];}

  // if the array has two items, join them with " and "
  if (teachers.length === 2) {return teachers[0] + ' and ' + teachers[1];}

  // if the array has more than two items
  const allButLast = teachers.slice(0, -1).join(', ');
  const last = teachers[teachers.length - 1];
  return `${allButLast}, and ${last}`;
});

const hasProgress = computed(() => props.progress !== 0);
const isWorkshop = computed(() => props.type === 'workshop');
const isCourse = computed(() => props.type === 'course');
function getSpecialityLabel (speciality: Speciality) {
  return SpecialityLabels[speciality] || speciality;
}

const classes = computed(() => {
  const arr = ['m-course-card', 'transition-all', `spec-border-${props.specialization[0]
    .toLowerCase()
    .replaceAll(' ', '-')}-h`];

  if (hasProgress.value) {
    arr.push('has-progress');
  }

  return arr;
});

const specBgColorClass = computed(() => `spec-bgcolor-${props.specialization[0]
  .toLowerCase()
  .replaceAll(' ', '-')}`);

const specialtyTagClass = computed(() => {
  const specialityFormatted = (props.specialization[0] ?? '').toLowerCase().replaceAll(' ', '-');
  return `spec-color-${specialityFormatted} spec-bgcolor-${specialityFormatted}-l`;
});
</script>

<template>
  <MCard
    :element="element ?? 'a'"
    :href="targetUrl"
    :class="classes"
  >
    <BadgeFreeCmeCourse
      v-if="showFreeCmeBadge"
      class="badge-free-cme"
    />

    <div class="top-container">
      <h3
        class="title overflow-hidden text-ellipsis break-words"
        :title="title"
      >
        {{title}}
      </h3>

      <div
        :title="authors"
        class="authors text-ellipsis overflow-hidden"
      >
        By {{authors}}
      </div>

      <div class="flex flex-nowrap gap-2 -ml-1">
        <Tag
          v-for="(speciality, index) in specialization"
          :key="index"
          :value="getSpecialityLabel(speciality)"
          :class="specialtyTagClass"
          class="truncate text-12 overflow-hidden"
          :pt="{
            value: {
              class: 'truncate'
            }
          }"
        />

        <Tag
          v-if="cmeCredits"
          class="flex items-center gap-2 bg-neutral-10 text-neutral-80 text-12"
        >
          <MIcon icon="cme" />
          <span class="truncate">{{cmeCredits}} CME</span>
        </Tag>
      </div>
    </div>

    <div
      v-if="!hasProgress && isWorkshop"
      class="meta-container meta-container--workshop"
    >
      <div class="meta-section leading-100">
        <MIcon
          icon="stethoscope"
          class="meta-icon"
        />
        <span>{{cases}}&nbsp;Cases</span>
      </div>
    </div>

    <div
      v-if="!hasProgress && isCourse"
      class="meta-container meta-container--course text-neutral-70"
    >
      <div class="meta-section leading-100">
        <MIcon
          icon="book-bookmark"
          class="meta-icon"
        />
        <span>{{lessons}}&nbsp;Lessons</span>
      </div>
      <div
        v-if="durationPretty"
        class="meta-section leading-100"
      >
        <MIcon
          icon="circle-play"
          class="meta-icon"
        />
        <span v-sanitize-html="durationPretty.replaceAll(' ', '&nbsp;')" />
      </div>
      <div
        v-if="quizzes"
        class="meta-section leading-100"
      >
        <MIcon
          icon="bolt"
          class="meta-icon"
        />
        <span>{{quizzes}}&nbsp;{{`Quiz${quizzes !== 1 ? 'zes' : ''}`}}</span>
      </div>
    </div>

    <div
      v-if="hasProgress"
      class="separator-and-progress"
    >
      <Divider class="mt-0 mb-3 w-[calc(100%+2.75em)] -ml-[calc(1.125em-2px)]" />

      <div class="flex items-center justify-between gap-2 w-full">
        <ProgressBar
          :value="progress ?? 0"
          class="w-full h-4"
          :pt="{
            value: {
              class: [
                progress === 100 ? 'bg-green-50' : 'bg-blue-60',
                '!transition-none'
              ]
            },
            label: {
              class: 'text-12'
            }
          }"
          :show-value="(progress ?? 0) >= 8"
        />
      </div>
    </div>

    <div
      :class="`media ${specBgColorClass}`"
      :style="{backgroundImage: `url(${backgroundImage})`}"
    >
      <img
        :src="teacherIllustration"
        :alt="title"
        draggable="false"
      >
    </div>
  </MCard>
</template>

<style lang="scss">
@import '../../assets/styles/utility.scss';
@import '../../assets/styles/font-styles.scss';

@mixin line-clamp($lines: 2) {
  display: -webkit-box;
  -webkit-line-clamp: $lines;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.m-card.m-course-card {
  display: inline-flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  min-height: 12rem;
  padding: 1rem 4rem 0.75rem 1rem;
  position: relative;
  background-color: white;
  border: 2px solid transparent;
  border-radius: 1rem;
  text-decoration: none;
  color: currentColor;

  @include breakpoint(medium) {
    padding-right: calc(12.5rem + 1.3rem);
  }

  .badge-free-cme {
    position: absolute;
    right: -0.7em;
    top: -0.7em;
    width: 3.2em;
    height: 3.2em;
    fill: white;
    box-sizing: border-box;
    z-index: 5;
  }

  .completed {
    position: absolute;
    right: 0.5em;
    top: 0.5em;
    width: 1.5em;
    height: 1.5em;
    fill: white;
    background: var(--palette-green-60);
    border-radius: 50%;
    padding: 0.3125em;
    box-sizing: border-box;
    box-shadow: 0 0 0 2px rgba(255,255,255,0.6);
    z-index: 1;
  }

  &.has-progress {
    padding-bottom: calc(1rem - 0.375em);
  }

  .top-container {
    margin-bottom: 1em;
  }

  .title {
    @include f-20-bold();
    @include line-clamp(4);
    margin: 0;
    margin-bottom: 0.375rem;
    line-height: 1.2;
    -webkit-line-clamp: 3;
    display: -webkit-box;
    -webkit-box-orient: vertical;
  }

  .authors {
    @include f-14-regular();
    @include line-clamp(2);
    margin-bottom: 0.5em;
    line-height: 1.429em;
    color: var(--palette-neutral-50);
    -webkit-line-clamp: 1;
    display: -webkit-box;
    -webkit-box-orient: vertical;
  }

  .meta-container {
    display: flex;
    font-size: 0.75rem;

    .meta-section {
      display: flex;
      align-items: center;
      margin-right: 0.75rem;
    }

    .meta-icon {
      margin-right: 0.375rem;
    }
  }

  .media {
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    width: 2.75rem;
    top: -2px;
    right: -2px;
    bottom: -2px;
    background-size: 12em;
    background-repeat: repeat;
    overflow: hidden;
    border-top-right-radius: 1rem;
    border-bottom-right-radius: 1rem;

    @include breakpoint(medium) {
      width: 12.5rem;
      top: 0.125rem * -1;
      right: 0.125rem * -1;
      bottom: 0.125rem * -1;
    }

    img {
      display: none;
      width: 100%;
      max-width: 70%;
      max-height: calc(100% - 2em);
      padding: 0;
      margin: 0;
      transform: translateY(-1em);
      object-fit: contain;
      color: var(--palette-neutral-70);

      @include breakpoint(medium) {
        display: block;
      }
    }
  }
}
</style>
